//*React
import { useEffect, useCallback, useMemo, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
//*Icons
import userPic from '../../../../../shared/icons/profil.svg';
import Flyout from './Flyout';
import HeaderHamburger from './HeaderHamburger';
import Logo from './Logo';
import Nav from './Nav';
import MenuButtonSwitcher from './MenuButtonSwitcher';
//*Reducers
import { toggleDesktopMenu, toggleMobileMenu } from '../../reducers/app';
import { setPageDialogOpenLoginForm, setResetFieldsLoginForm } from '../../reducers/loginForm';
import { setPageDialogOpenRegisterForm, setResetFieldsRegisterForm } from '../../reducers/registerForm';

// DEMO
import { isDemoUrl } from '../../util/generalUtils';
import { demoUserObject } from '../../demodata/demouser.js';

// Only memoize Nav since it's likely the most complex component
const MemoizedNav = memo(Nav);

function Header({ cssClasses = '', children, overlappedContent }) {
  // Use selector with more specific selections to prevent unnecessary rerenders
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const firstName = useSelector((state) => state.user.firstName);
  const lastName = useSelector((state) => state.user.lastName);
  const desktopMenuOpen = useSelector((state) => state.app.desktopMenuOpen);

  const dispatch = useDispatch();

  // Memoize event handlers
  const handleOpenRegisterForm = useCallback(() => {
    dispatch(setResetFieldsRegisterForm());
    dispatch(setPageDialogOpenRegisterForm(true));
  }, [dispatch]);

  const handleOpenLoginForm = useCallback(() => {
    dispatch(setResetFieldsLoginForm());
    dispatch(setPageDialogOpenLoginForm(true));
  }, [dispatch]);

  const handleOpenFlyout = useCallback(() => {
    if (!desktopMenuOpen) {
      dispatch(toggleDesktopMenu());
    }
  }, [desktopMenuOpen, dispatch]);

  // Memoize things that don't change often
  const isDemo = useMemo(() => isDemoUrl(), []);

  // Memoize parts of the UI that don't need to rerender when other parts change
  const headerClassName = useMemo(() => {
    return overlappedContent ? `header--overlapped ${cssClasses}` : cssClasses;
  }, [overlappedContent, cssClasses]);

  // Memoize user picture component
  const UserPicture = useCallback(
    () => (
      <div className="header__userpic" onClick={handleOpenFlyout}>
        <img src={userPic} alt={isDemo ? `${demoUserObject.firstName} ${demoUserObject.lastName}` : `${firstName} ${lastName}`} className="image--round" />
      </div>
    ),
    [isDemo, firstName, lastName, handleOpenFlyout],
  );

  // Memoize the right side buttons section based on login state
  const ButtonRightSection = useMemo(() => {
    if (isDemo) {
      return (
        <div className="header__buttonright">
          <MenuButtonSwitcher />
          <UserPicture />
          <HeaderHamburger />
        </div>
      );
    }

    if (isLoggedIn) {
      return (
        <div className="header__buttonright">
          <MenuButtonSwitcher />
          <UserPicture />
          <HeaderHamburger />
        </div>
      );
    }

    return (
      <div className="header__buttonright">
        <button className="button button--white button--login-menu" onClick={handleOpenLoginForm}>
          Login
        </button>
        <button className="button button--white-outline button--register-menu" onClick={handleOpenRegisterForm}>
          Registrieren
        </button>
        <HeaderHamburger />
      </div>
    );
  }, [isDemo, isLoggedIn, handleOpenLoginForm, handleOpenRegisterForm]);

  return (
    <>
      {desktopMenuOpen && <Flyout isDemo={isDemo} />}
      <div id="header" className={headerClassName}>
        <header id="sreal-header">
          <Logo cssClasses="header__logo" />
          {ButtonRightSection}
          <MemoizedNav />
        </header>
        {children}
      </div>
    </>
  );
}

// Wrap the entire component in memo for extra optimization
export default memo(Header);
